"use client";

import { Strong, Text, TextLink } from "@/components/catalyst/text";
import { PasswordField } from "@/components/auth/password-field";
import { SocialSignIn } from "@/components/auth/social-sign-in";
import { CheckboxField } from "@/components/catalyst/checkbox";
import { EmailField } from "@/components/auth/email-field";
import { Checkbox } from "@/components/catalyst/checkbox";
import { Divider } from "@/components/catalyst/divider";
import { useAuthForm } from "@/lib/hooks/use-auth-form";
import { Label } from "@/components/catalyst/fieldset";
import { AuthForm } from "@/components/auth/auth-form";
import { Button } from "@/components/catalyst/button";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { signIn } from "@/lib/auth/client";
import { useForm } from "react-hook-form";
import { Suspense } from "react";
import { z } from "zod";

function getFormSchema(t: ReturnType<typeof useTranslations>) {
  return z.object({
    email: z.email({
      message: t("emailValidation"),
    }),
    password: z.string().min(1, { message: t("passwordRequired") }),
    rememberMe: z.boolean().optional(),
  });
}

function LoginForm() {
  const t = useTranslations("Login");
  const { errorMessage, onSuccess, onError, clearError, redirectUrl } =
    useAuthForm();

  const formSchema = getFormSchema(t);
  type SignInFormValues = z.infer<typeof formSchema>;

  const onSubmit = async ({
    email,
    password,
    rememberMe,
  }: SignInFormValues) => {
    clearError();
    return signIn.email(
      {
        email,
        password,
        rememberMe,
      },
      { onSuccess, onError },
    );
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignInFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  return (
    <AuthForm
      title={t("title")}
      errorMessage={errorMessage}
      onSubmit={handleSubmit(onSubmit)}
    >
      <SocialSignIn disabled={isSubmitting} />
      <Text>
        {t.rich("agreement", {
          terms: (chunks) => (
            <TextLink target="_blank" href="/terms">
              {chunks}
            </TextLink>
          ),
          privacy: (chunks) => (
            <TextLink target="_blank" href="/privacy">
              {chunks}
            </TextLink>
          ),
          acceptableUse: (chunks) => (
            <TextLink target="_blank" href="/acceptable-use">
              {chunks}
            </TextLink>
          ),
        })}
      </Text>
      <div className="flex items-center gap-4">
        <Divider />
        <Text className="text-sm text-zinc-500">{t("or")}</Text>
        <Divider />
      </div>
      <EmailField
        register={register("email")}
        error={errors.email}
        disabled={isSubmitting}
        testId="login-email-input"
      />
      <PasswordField
        register={register("password")}
        error={errors.password}
        disabled={isSubmitting}
        testId="login-password-input"
      />
      <div className="flex items-center justify-between">
        <CheckboxField>
          <Checkbox name="rememberMe" disabled={isSubmitting} />
          <Label>{t("rememberMe")}</Label>
        </CheckboxField>
        <Text>
          <TextLink href="/reset-password">
            <Strong>{t("forgotPassword")}</Strong>
          </TextLink>
        </Text>
      </div>
      <Button
        type="submit"
        className="w-full"
        disabled={isSubmitting}
        color="purple"
        data-testid="login-submit-button"
      >
        {isSubmitting ? t("loggingIn") : t("login")}
      </Button>
      <Text>
        {t("dontHaveAccount")}{" "}
        <TextLink
          href={
            redirectUrl
              ? `/register?redirectUrl=${encodeURIComponent(redirectUrl)}`
              : "/register"
          }
        >
          <Strong>{t("signUp")}</Strong>
        </TextLink>
      </Text>
    </AuthForm>
  );
}

export default function Login() {
  const t = useTranslations("Login");

  return (
    <Suspense fallback={<div>{t("loading")}</div>}>
      <LoginForm />
    </Suspense>
  );
}
